import React, { useState, useEffect, useRef, type CSSProperties } from 'react'
import ReactDOM from 'react-dom'
import * as Page from '@Utils/BrowserDimensions'
import find from 'lodash/find'
import map from 'lodash/map'
import filter from 'lodash/filter'
import classnames from 'classnames'
import Tooltip from '@SiteComponents/Tooltip'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import Checkmark from '@Images/finetune_parameters_checkmark.svg'

import {
  IProjectVersionSettingsParameter,
} from '@SiteModules/ProjectVersionSettings/types'

import './ButtonParameter.scss'

const DROPDOWN_OFFSET = 7 // px
const SORT_LIMIT = 7 // items. For more than these items, the selected option appears first

// Values from ButtonParameter.scss
const DROPDOWN_MAX_HEIGHT = 335 // px
const DROPDOWN_OPTION_HEIGHT = 40 // px
const DROPDOWN_OPTION_CONTAINER_GAP = 4 // px
const DROPDOWN_OPTION_CONTAINER_PADDING = 4 // px
const SEARCH_INPUT_HEIGHT = 43 // px

const TOOLTIP_OFFSET = 13 // px

interface ButtonParameterProps {
  readonly parameter: IProjectVersionSettingsParameter
  readonly classNameOfContainerToMakeUnscrollable: string
}

const ButtonParameter: React.FC<ButtonParameterProps> = props => {
  const {
    name,
    searchPlaceholder,
    icon,
    options,
    nullOption,
    selectedOptionName,
    selectedOptionNameButton,
    setSelectedOption,
    largeIcon,
    specialButtonClickBehaviour,
    tooltipText,
    showAsAdvanced,
    isActive,
    dropdownDirection = 'auto',
  } = props.parameter

  const {
    showTooltip,
    triggerShowTooltip,
    cancelTooltip,
  } = useTooltipDelay()

  const [optionSearchText, setOptionSearchText] = useState('')
  const [relevantOptions, setRelevantOptions] = useState(options)
  const [showDropdown, setShowDropdown] = useState(false)
  const [nullByDefault, setNullByDefault] = useState(nullOption && selectedOptionName === nullOption.name)
  const [tooltipHeight, setTooltipHeight] = useState<number | null>(null)
  const [tooltipWidth, setTooltipWidth] = useState<number | null>(null)

  const buttonRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const selectedOption = find(options, option => option.name === selectedOptionName)
  const classNameForClickOutsideListener = `ButtonParameter__${name.toLowerCase().replace(/\s+/g, '-')}`
  const nullOptionSelected = selectedOptionName === null
  const shouldShowSearchInput = !!searchPlaceholder
  const hasNullOption = !!nullOption
  const numberOfOptions = hasNullOption ? options.length + 1 : options.length
  const shouldSort = numberOfOptions > SORT_LIMIT
  const shouldShowTooltip = showTooltip && !!tooltipText && !showDropdown

  let buttonText = name
  if (selectedOptionName) {
    buttonText = selectedOptionNameButton || selectedOptionName
  } else if (nullOptionSelected && !nullByDefault) {
    buttonText = nullOption!.name
  }

  let buttonIcon = icon
  if (selectedOption && selectedOption.icon) {
    buttonIcon = selectedOption.icon
  } else if (nullOptionSelected && !nullByDefault && nullOption!.icon) {
    buttonIcon = nullOption!.icon
  }

  useEffect(() => {
    const newRelevantOptions = filter(options, option => option.name.toLowerCase().includes(optionSearchText.toLowerCase()))
    setRelevantOptions(newRelevantOptions)
  }, [options, optionSearchText])

  useEffect(() => {
    const container = document.getElementsByClassName(props.classNameOfContainerToMakeUnscrollable)
    if (container && container.length > 0) {
      const containerElement = container[0] as HTMLElement
      containerElement.style.overflowY = showDropdown ? 'hidden' : 'scroll'
    }

    if (showDropdown && showTooltip) {
      cancelTooltip()
    }
  }, [showDropdown])

  useEffect(() => {
    if (!showTooltip) {
      setTooltipHeight(null)
      setTooltipWidth(null)
      return
    }

    const timer = setTimeout(() => {
      if (tooltipRef.current) {
        const rect = tooltipRef.current.getBoundingClientRect()
        setTooltipHeight(rect.height)
        setTooltipWidth(rect.width)
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [showTooltip])

  useClickOutsideListener(classNameForClickOutsideListener, setShowDropdown, showDropdown)

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const onMouseEnterButton = () => {
    triggerShowTooltip()
  }

  const onMouseLeaveButton = () => {
    cancelTooltip()
  }

  const handleOptionSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = (e.target as HTMLInputElement).value
    setOptionSearchText(searchText)
  }

  const sortedOptions = [...relevantOptions].sort((a, b) => {
    if (a.name === selectedOptionName) { return -1 }
    if (b.name === selectedOptionName) { return 1 }
    return 0
  })

  const parameterOptions = map(shouldSort ? sortedOptions : relevantOptions, (option, index) => {
    const selected = selectedOptionName === option.name
    const hasIcon = !!option.icon

    const handleSetSelectedOption = () => {
      if (selected) { return }

      setSelectedOption(option.name)
      setShowDropdown(false)
    }

    return (
      <li
        key={index}
        className={classnames(
          'ButtonParameter__dropdown-option',
          { 'ButtonParameter__dropdown-option-unselected': !selected }
        )}
        onClick={handleSetSelectedOption}
      >
        {hasIcon && (
          <div className="ButtonParameter__dropdown-option-icon">
            {option.icon}
          </div>
        )}
        <div className="ButtonParameter__dropdown-option-name-wrapper">
          <div className="ButtonParameter__dropdown-option-name-and-description">
            <div className="ButtonParameter__dropdown-option-name">
              {option.name}
            </div>
            {option.description && (
              <div className="ButtonParameter__dropdown-option-description">
                {option.description}
              </div>
            )}
          </div>
          {typeof option.number === 'number' && (
            <div className="ButtonParameter__dropdown-option-item-number">
              {option.number === 1 ? '1 item' : `${option.number} items`}
            </div>
          )}
          {option.trailingIcon}
        </div>
        {selected && (
          <div className="ButtonParameter__dropdown-option-checkmark">
            <Checkmark />
          </div>
        )}
      </li>
    )
  })

  const renderNullOption = () => {
    const hasIcon = !!nullOption!.icon

    const handleSetSelectedOption = () => {
      if (nullOptionSelected) { return }

      setSelectedOption(null)
      setNullByDefault(false)
      setShowDropdown(false)
    }

    return (
      <li
        key={0}
        className={classnames(
          'ButtonParameter__dropdown-option',
          { 'ButtonParameter__dropdown-option-unselected': !nullOptionSelected }
        )}
        onClick={handleSetSelectedOption}
      >
        {hasIcon && (
          <div className="ButtonParameter__dropdown-option-icon">
            {nullOption!.icon}
          </div>
        )}
        <div className="ButtonParameter__dropdown-option-name">
          {nullOption!.name}
        </div>
        {nullOptionSelected && (
          <div className="ButtonParameter__dropdown-option-checkmark">
            <Checkmark />
          </div>
        )}
      </li>
    )
  }

  const renderSearchInput = () => {
    return (
      <div className="ButtonParameter__dropdown-input-wrapper">
        <input
          placeholder={searchPlaceholder}
          autoComplete="off"
          value={optionSearchText}
          onChange={handleOptionSearchTextChange}
        />
      </div>
    )
  }

  const renderTooltip = () => {
    if (!showTooltip) {
      return null
    }

    const buttonBounds = buttonRef.current
      ? buttonRef.current.getBoundingClientRect()
      : { x: 0, y: 0, width: 0, height: 0 }

    const tooltipX = tooltipWidth != null
      ?  buttonBounds.x - 0.5 * tooltipWidth + 0.5 * buttonBounds.width
      : buttonBounds.x

    const tooltipY =
      tooltipHeight != null
        ? buttonBounds.y - tooltipHeight - TOOLTIP_OFFSET
        : buttonBounds.y

    const style: CSSProperties = {
      position: 'fixed',
      top: `${tooltipY}px`,
      left: `${tooltipX}px`,
      visibility: tooltipHeight == null || tooltipWidth === null ? 'hidden' : 'visible',
      pointerEvents: tooltipHeight == null || tooltipWidth === null ? 'none' : 'auto',
      opacity: tooltipHeight == null || tooltipWidth === null ? 0 : 1,
      transition: 'opacity 0.2s ease'
    }

    return ReactDOM.createPortal(
      <div
        className="ButtonParameter__tooltip-container"
        ref={tooltipRef}
        style={style}
      >
        <Tooltip
          text={tooltipText}
          position="bottom"
        />
      </div>,
      document.getElementById('site-container')!
    )
  }

  const renderDropdown = () => {
    const buttonBounds = buttonRef.current ? buttonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }
    const optionHeightWithMargin = DROPDOWN_OPTION_HEIGHT + DROPDOWN_OPTION_CONTAINER_GAP
    const optionsHeight = Math.min(
      (numberOfOptions - 1) * optionHeightWithMargin + DROPDOWN_OPTION_HEIGHT + DROPDOWN_OPTION_CONTAINER_PADDING * 2,
      DROPDOWN_MAX_HEIGHT
    )
    const searchHeight = shouldShowSearchInput ? SEARCH_INPUT_HEIGHT : 0
    const totalHeight = searchHeight + optionsHeight
    const dropdownX = buttonBounds.x
    const dropdownYBelow = Math.min(
      buttonBounds.y + buttonBounds.height + DROPDOWN_OFFSET,
      Page.getHeight() - totalHeight - DROPDOWN_OFFSET
    )
    const shouldRenderDropdownAbove = dropdownDirection === 'up' || (
      buttonBounds.y + buttonBounds.height + DROPDOWN_OFFSET + totalHeight > Page.getHeight() &&
      buttonBounds.y - totalHeight - DROPDOWN_OFFSET >= 0
    )
    const style = shouldRenderDropdownAbove
      ? {
          top: `${Math.max(DROPDOWN_OFFSET, buttonBounds.y - DROPDOWN_OFFSET)}px`,
          left: `${dropdownX}px`,
          transform: 'translateY(-100%)',
        }
      : {
          top: `${dropdownYBelow}px`,
          left: `${dropdownX}px`,
        }

    const noOptionFound = relevantOptions.length === 0 && optionSearchText.length > 0

    return ReactDOM.createPortal(
      (
        <div
          className={`ButtonParameter__dropdown ${classNameForClickOutsideListener}`}
          style={style}
        >
          {shouldShowSearchInput && renderSearchInput()}
          <ul className="ButtonParameter__dropdown-options">
            {hasNullOption && renderNullOption()}
            {parameterOptions}
            {noOptionFound && (
              <li
                key={1}
                className="ButtonParameter__dropdown-option ButtonParameter__dropdown-option-not-found">
                No option found
              </li>
            )}
          </ul>
        </div>
      ),
      document.getElementById('site-container')!
    )
  }

  return (
    <div
      className={`ButtonParameter ${classNameForClickOutsideListener}`}
    >
      <div
        className={classnames(
          'ButtonParameter__button',
          { 'ButtonParameter__button-dropdown-open': showDropdown },
          { 'ButtonParameter__button-large-icon': largeIcon },
          { 'ButtonParameter__button-null-by-default': nullOptionSelected && nullByDefault },
          { 'ButtonParameter__button-advanced-inactive': showAsAdvanced && !isActive }
        )}
        onClick={specialButtonClickBehaviour || toggleDropdown}
        ref={buttonRef}
        onMouseEnter={onMouseEnterButton}
        onMouseLeave={onMouseLeaveButton}
      >
        {buttonIcon}
        <div className="ButtonParameter__button-text">
          {buttonText}
        </div>
      </div>
      {showDropdown && renderDropdown()}
      {shouldShowTooltip && renderTooltip()}
    </div>
  )
}

export default ButtonParameter
